import { useCallback, useState } from "react";

import { isApolloError, useApolloClient } from "@apollo/client";

import { TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useCheckAlreadyCompletedToast } from "flows/Inventory/shared/hooks/useCheckAlreadyCompletedToast";
import { useChecksRedirect } from "flows/Inventory/shared/hooks/useChecksRedirect";
import { useStartNextCheck } from "flows/Inventory/shared/hooks/useStartNextCheck";
import { useTaskCompletedToast } from "flows/Inventory/shared/hooks/useTaskCompletedToast";
import {
  ValidateStockCheckDocument,
  ValidateStockCheckMutation,
  ValidateStockCheckMutationVariables,
} from "flows/Inventory/shared/queries/stockCheck/stockCheck.generated";
import { ErrorTypeForAnalytics, useAnalytics } from "shared/hooks/useAnalytics";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { reportErrorsToDDAndThrow } from "utils/datadog";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

type ValidateIDCArgs = {
  checkId: string | null;
  sku?: string | null;
  quantity: number;
  expectedQuantity: number;
  shelfNumber?: string | null;
  shelfLetter?: string | null;
};

export function useIDCService() {
  const client = useApolloClient();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { navigateToCheck, navigateToOrigin } = useChecksRedirect();
  const { startNextCheck } = useStartNextCheck();
  const showTaskCompletedToast = useTaskCompletedToast();
  const { showCheckAlreadyCompletedToast } = useCheckAlreadyCompletedToast();
  const { showToastAndPlayError } = useErrorToastWithAudio({ errorToastId: "validate_idc_error" });

  const [countedItems, setCountedItems] = useState<number>(0);

  const mutateIDCCheck = useCallback(
    async ({
      checkId,
      sku,
      quantity,
      expectedQuantity,
    }: Omit<ValidateIDCArgs, "shelfNumber" | "shelfLetter"> & {
      checkId: string;
    }): Promise<void> => {
      return reportErrorsToDDAndThrow(`updating IDC ${checkId}`, async () => {
        try {
          const response = await client.mutate<
            ValidateStockCheckMutation,
            ValidateStockCheckMutationVariables
          >({
            mutation: ValidateStockCheckDocument,
            variables: { input: { checkId, quantity } },
          });
          if (response.data?.validateStockCheck.success) {
            sendSegmentTrackEvent("stockCheckFinished", {
              type: "automated",
              check_id: checkId,
              product_sku: sku,
              quantity_expected: expectedQuantity,
              quantity_counted: quantity,
              quantity_damaged: 0,
              quantity_expired: 0,
              quantity_tgtg: 0,
            });
          }
        } catch (err: any) {
          if (isApolloError(err)) {
            const errorCode = err?.graphQLErrors?.[0]?.extensions?.code;
            if (errorCode === "TASK_UPDATE_CONFLICT") {
              showCheckAlreadyCompletedToast();
            } else {
              showToastAndPlayError();
            }
            throw err;
          }
        }
      });
    },
    [client, sendSegmentTrackEvent, showCheckAlreadyCompletedToast, showToastAndPlayError],
  );

  const startNextIDCCheck = useCallback(
    async (shelfNumber?: string | null, shelfLetter?: string | null): Promise<void> => {
      const data = await startNextCheck({
        groupedPriority: [],
        shelfNumber: `${shelfNumber ?? ""}${shelfLetter ?? ""}`,
        type: [TaskType.inventory_discrepancy_check],
      });

      if ("check" in data && isNotNullNorUndefined(data.check)) {
        navigateToCheck(data.check, PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE);
      } else {
        if ("status" in data && data.status === "noMoreCheck") {
          showTaskCompletedToast();
        }
        navigateToOrigin(PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE);
      }
    },
    [navigateToCheck, navigateToOrigin, startNextCheck, showTaskCompletedToast],
  );

  const validateIDC = useCallback(
    async ({
      checkId,
      sku,
      quantity,
      expectedQuantity,
      shelfNumber,
      shelfLetter,
    }: ValidateIDCArgs): Promise<void> => {
      try {
        if (isNullOrUndefined(checkId)) throw new Error("No checkId provided");
        await mutateIDCCheck({ checkId, quantity, sku, expectedQuantity });
        await startNextIDCCheck(shelfNumber, shelfLetter);
      } catch (err: any) {
        if (!isApolloError(err as Error)) throw err;

        showToastAndPlayError();
        navigateToOrigin(PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE);

        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE,
          component_value: ErrorTypeForAnalytics.UNKNOWN,
        });
      } finally {
        setCountedItems(0);
      }
    },
    [
      mutateIDCCheck,
      startNextIDCCheck,
      navigateToOrigin,
      showToastAndPlayError,
      sendSegmentTrackEvent,
    ],
  );

  return {
    countedItems,
    setCountedItems,
    validateIDC,
  };
}
